import Box from '@app/components/common/Box';
import Icon from '@app/components/common/Icon';
import SelectionModal from '@app/components/common/SelectionModal';
import { IItemSelection } from '@app/components/common/SelectionModal/GenericSelectionModal/types';
import Typography from '@app/components/common/Typography';
import { selectRoleCardsList } from '@app/state/selectors/settingsSelectors';
import { selectLoggedInRole } from '@app/state/users/usersSelectors';
import { useMemo, useState } from 'react';
import { WithTranslation } from 'react-i18next';
import { columnHelper, ROW_ID_PREFIX } from './types';
import Table from '@app/components/common/Table';
import NameLink from '@app/components/LoggedIn/Menu/ModifierGroups/ModifierGroupsTable/NameLink';
import MoneyInput from '@app/components/common/MoneyInput';
import Button from '@app/components/common/Button';
import { formatMoney } from '@westondev/tableturn-core';
import { useSelector } from 'react-redux';

interface IRolesSubsection extends WithTranslation {
  roleIds: number[];
  isCreationMode: boolean;
  onAssociate: (roleIds: IItemSelection[]) => void;
  onDisassociate: (id: number) => void;
}

const RolesSubsection = ({
  roleIds,
  onAssociate,
  onDisassociate,
  isCreationMode,
  t,
}: IRolesSubsection) => {
  const cardRolesList = useSelector(selectRoleCardsList);
  const loggedInRole = useSelector(selectLoggedInRole);

  const [openAddExistingRole, setOpenAddExistingRole] = useState(false);

  //TODO New properties should come directly from core Factory
  const userRolesWithMockData = useMemo(
    () =>
      cardRolesList
        .filter(role => roleIds.includes(role.id))
        .map(role => {
          return {
            ...role,
            payBasis: 'Hourly',
            defaultPay: 0,
            overridePay: 0,
            canDelete: !role.superAdmin,
          };
        }),
    [cardRolesList, roleIds],
  );

  const handleLocationsAssociation = (selectedRoles: IItemSelection[]) => {
    onAssociate(selectedRoles);
    setOpenAddExistingRole(false);
  };

  const COLUMN_VALUES = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: t('settingsModule.usersSettings.rolesTable.headers.role'),
        cell: info => {
          return (
            <Box csx={{ display: 'flex' }}>
              <NameLink
                name={info.getValue()}
                path={`/settings/accounts/roles/${info.row.original.id}`}
                status={info.row.original.active}
                statusPath=""
              />
            </Box>
          );
        },
        minSize: 200,
      }),
      columnHelper.accessor('payBasis', {
        header: t('settingsModule.usersSettings.rolesTable.headers.payBasis'),
        cell: info => <Typography>{info.row.original.payBasis}</Typography>, //TODO add translation to this value
        minSize: 200,
      }),
      columnHelper.accessor('defaultPay', {
        header: t('settingsModule.usersSettings.rolesTable.headers.defaultPay'),
        cell: info => (
          <Typography>
            {formatMoney('', info.row.original.defaultPay)}
          </Typography>
        ),
        minSize: 200,
      }),
      columnHelper.accessor('overridePay', {
        header: t(
          'settingsModule.usersSettings.rolesTable.headers.overridePay',
        ),
        cell: info => (
          <Box csx={[{ maxWidth: '50%' }]}>
            <MoneyInput
              required
              placeholder={t(
                'settingsModule.usersSettings.rolesTable.headers.overridePay',
              )}
              value={info.getValue()}
              precision={2}
              onValueChange={() => null}
            />
          </Box>
        ),
        minSize: 200,
      }),
      columnHelper.accessor('canDelete', {
        header: t('settingsModule.usersSettings.rolesTable.headers.delete'),
        cell: info => {
          const canDelete =
            isCreationMode || loggedInRole.superAdmin ? true : info.getValue();
          return (
            <Box csx={[{ maxWidth: '50%' }]}>
              <Button
                variant="danger"
                csx={{ width: '50px' }}
                icon={<Icon name="MdDeleteForever" />}
                disabled={!canDelete}
                onClick={() => onDisassociate(info.row.original.id)}
              />
            </Box>
          );
        },
        size: 70,
      }),
    ],
    [t, isCreationMode, loggedInRole, onDisassociate],
  );

  return (
    <>
      <SelectionModal
        type="roles"
        onAssociate={handleLocationsAssociation}
        onModalClose={() => setOpenAddExistingRole(false)}
        active={openAddExistingRole}
        allowAssociateMultiple
        showDescriptionPanel={false}
        btnSuccessText={t('commonButtons.associate')}
        showSelectedButtons={true}
      />
      <Table
        scrollEnabled
        nestedScrollEnabled
        data={userRolesWithMockData}
        columns={COLUMN_VALUES}
        rowIdPrefix={ROW_ID_PREFIX}
        cellCsx={{ height: '70px' }}
        renderEmptyValues
      />
      <Box
        csx={{
          marginTop: '20px',
        }}>
        <Button
          variant="primary"
          icon={<Icon name="MdAddCircle" />}
          csx={[
            {
              width: '100%',
              minWidth: '50px',
              fontWeight: '500',
            },
          ]}
          onClick={() => setOpenAddExistingRole(true)}>
          {t('settingsModule.usersSettings.rolesTable.addRoleButton')}
        </Button>
      </Box>
    </>
  );
};

export default RolesSubsection;
